import { useEffect, useMemo, useState } from "react";
import { addMasterRecord, addPartRecord } from "../api";
import { S } from "../styles";
import { InputField, useCompactLayout } from "../ui";

const QUICK_ADD_CONFIG = {
  parts: {
    table: "parts",
    label: "Item",
    pluralLabel: "Items",
    description: "Add new production items so the latest SKU is available on the floor.",
  },
  operators: {
    table: "operators",
    label: "Operator",
    pluralLabel: "Operators",
    description: "Add operator names so supervisors can record production without waiting.",
  },
};

export function QuickAddScreen({ masters, refreshMasters, showToast }) {
  const isCompactLayout = useCompactLayout();
  const [tab, setTab] = useState("parts");
  const [draft, setDraft] = useState("");
  const [partTarget, setPartTarget] = useState("");
  const [saving, setSaving] = useState(false);

  const config = QUICK_ADD_CONFIG[tab];
  const currentList = useMemo(() => masters[tab] || [], [masters, tab]);

  useEffect(() => {
    setDraft("");
    setPartTarget("");
  }, [tab]);

  const handleAdd = async () => {
    if (!draft.trim()) {
      showToast(`Enter a ${config.label.toLowerCase()} name first.`, "error");
      return;
    }

    if (tab === "parts" && !partTarget.trim()) {
      showToast("Target Qty / 10 hrs is required for every item.", "error");
      return;
    }

    setSaving(true);
    try {
      if (tab === "parts") {
        await addPartRecord(draft, partTarget);
        setPartTarget("");
      } else {
        await addMasterRecord(config.table, draft);
      }
      setDraft("");
      showToast(`${config.label} added successfully.`);
      await refreshMasters();
    } catch (error) {
      showToast(error.message || `Unable to add ${config.label.toLowerCase()}.`, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-form-type="other" data-lpignore="true" style={S.sectionCard}>
      <div style={S.sectionHeader}>
        <div>
          <div style={S.sectionTitle}>Quick Add</div>
          <div style={S.subtitle}>
            Supervisors can add new items and operators. Deletion stays in the admin area.
          </div>
        </div>
        {saving && <span style={{ color: "#60708a", fontWeight: 700 }}>Saving...</span>}
      </div>

      <div style={S.pillRow}>
        {Object.entries(QUICK_ADD_CONFIG).map(([value, item]) => (
          <button
            key={value}
            style={{ ...S.pillBtn, ...(tab === value ? S.pillActive : {}) }}
            onClick={() => setTab(value)}
          >
            {item.pluralLabel}
          </button>
        ))}
      </div>

      <div style={{ ...S.formGrid, marginTop: 18 }}>
        <InputField
          label={`New ${config.label}`}
          placeholder={`Enter ${config.label.toLowerCase()} name`}
          value={draft}
          onChange={(value) =>
            setDraft(tab === "parts" ? value.toUpperCase() : value)
          }
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleAdd();
            }
          }}
        />
        {tab === "parts" && (
          <InputField
            inputMode="numeric"
            label="Target Qty / 10 hrs"
            placeholder="e.g. 1000"
            type="number"
            value={partTarget}
            onChange={setPartTarget}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleAdd();
              }
            }}
          />
        )}
        <div style={{ display: "flex", alignItems: "end" }}>
          <button
            style={{
              ...S.primaryBtn,
              ...(isCompactLayout ? { width: "100%" } : {}),
            }}
            onClick={handleAdd}
          >
            Add {config.label}
          </button>
        </div>
      </div>

      <div
        style={{
          ...S.pageBody,
          marginTop: 12,
        }}
      >
        {config.description}
      </div>

      <div style={{ ...S.listCard, marginTop: 18 }}>
        {currentList.length === 0 ? (
          <div style={S.emptyState}>No {config.pluralLabel.toLowerCase()} added yet.</div>
        ) : (
          currentList.map((item) => (
            <div key={item.id} style={S.listItem}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.cardTitle}>{item.label}</div>
                {tab === "parts" && (
                  <div style={{ ...S.pageBody, marginTop: 4 }}>
                    Target:{" "}
                    {item.targetPer10hrs
                      ? `${Number(item.targetPer10hrs).toLocaleString("en-IN")} / 10 hrs`
                      : "Not set"}
                  </div>
                )}
              </div>
              <span
                style={{
                  ...S.badge,
                  color: "#17355d",
                  background: "#e8f0ff",
                  width: isCompactLayout ? "100%" : "auto",
                  justifyContent: "center",
                }}
              >
                Available
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
